import { ChevronDown, ExternalLink } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { type AdminAgentRunExecutionRead } from '@libraries/typescript/api_client';

function executionSourceLabel(
    source: AdminAgentRunExecutionRead['braintrust_link_source'],
): string {
    if (source === 'execution') {
        return 'Exact execution';
    }
    if (source === 'run_fallback') {
        return 'Run fallback';
    }
    return 'Historical execution';
}

export function BraintrustExecutionLinks({
    executions,
    runBraintrustUrl,
}: {
    readonly executions: ReadonlyArray<AdminAgentRunExecutionRead>;
    readonly runBraintrustUrl: string | null;
}) {
    if (executions.length === 0 && runBraintrustUrl === null) {
        return null;
    }

    const newestFirstExecutions = executions
        .map((execution, index) => ({
            execution,
            ordinal: index + 1,
        }))
        .reverse();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button type="button" variant="outline" size="sm">
                    Braintrust
                    <ChevronDown className="size-3.5" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Executions</DropdownMenuLabel>
                {newestFirstExecutions.length === 0 ? (
                    <DropdownMenuItem disabled={true}>
                        No executions
                    </DropdownMenuItem>
                ) : (
                    newestFirstExecutions.map(({ execution, ordinal }) => {
                        const itemContent = (
                            <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-medium">
                                        Execution {ordinal}
                                    </div>
                                    <div className="text-muted-foreground truncate text-xs">
                                        {executionSourceLabel(
                                            execution.braintrust_link_source,
                                        )}
                                        {execution.model
                                            ? ` · ${execution.model}`
                                            : null}
                                    </div>
                                </div>
                                {execution.braintrust_url ? (
                                    <ExternalLink className="size-3.5" />
                                ) : (
                                    <span className="text-muted-foreground text-xs">
                                        Unavailable
                                    </span>
                                )}
                            </div>
                        );

                        return execution.braintrust_url ? (
                            <DropdownMenuItem
                                key={execution.execution_id}
                                asChild={true}
                            >
                                <a
                                    href={execution.braintrust_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {itemContent}
                                </a>
                            </DropdownMenuItem>
                        ) : (
                            <DropdownMenuItem
                                key={execution.execution_id}
                                disabled={true}
                            >
                                {itemContent}
                            </DropdownMenuItem>
                        );
                    })
                )}
                <DropdownMenuSeparator />
                {runBraintrustUrl ? (
                    <DropdownMenuItem asChild={true}>
                        <a
                            href={runBraintrustUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <span className="flex-1">Run-level trace</span>
                            <ExternalLink className="size-3.5" />
                        </a>
                    </DropdownMenuItem>
                ) : (
                    <DropdownMenuItem disabled={true}>
                        <span className="flex-1">Run-level trace</span>
                        <span className="text-muted-foreground text-xs">
                            Unavailable
                        </span>
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
